import {HierarchyEmployee, HierarchyNode, HierarchyRoot} from "../../util/EmployeeHierarchy";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {Box, IconButton, Stack, styled, TableCell, Tooltip} from "@mui/material";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../components/employee-link/EmployeeLink";
import React, {useMemo} from "react";
import {SHOW_ON_HOVER_CLASS_NAME, VirtualTable} from "../../components/data-table/VirtualTable";
import TeamComponent from "../../components/team-component/TeamComponent";
import AgricultureIcon from '@mui/icons-material/Agriculture';
import {EmployeeTreeTableContext, SeatingPlanNeighbourhoodSelect} from "./SeatingPlanNeighbourhoodSelect";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {SetState} from "../../util/FormHandlers";
import {
    GuessNeighbourhoodForm,
    GuessNeighbourhoodPicker
} from "../../components/guess-neighbourhood-dialog/GuessNeighbourhoodPicker";
import {RemoteValue} from "../../util/RemoteValue";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {badgeOrDefault} from "../../api/WorkSiteMapStore";

export type TreeRow = {
    node: HierarchyEmployee,
    depth: number,
};

function flattenTree(root: HierarchyNode): TreeRow[] {
    const rows: TreeRow[] = [];

    const stack: { node: HierarchyEmployee, depth: number }[] = [];
    for (let i = root.children.length - 1; i >= 0; i--) {
        stack.push({node: root.children[i], depth: 0});
    }

    while (stack.length > 0) {
        const row = stack.pop()!;
        rows.push(row);
        for (let i = row.node.children.length - 1; i >= 0; i--) {
            stack.push({node: row.node.children[i], depth: row.depth + 1});
        }
    }

    return rows;
}

const StyledTableCell = styled(TableCell, {
    shouldForwardProp: (prop) => prop !== 'match',
})<{ match: boolean }>(({match}) => ({
    verticalAlign: 'top',
    opacity: match ? 1 : 0.5,
    border: 'none',
}));

function MoveButton({node}: {node: HierarchyEmployee}) {
    const {model} = React.useContext(EmployeeTreeTableContext);
    if (!model) throw new Error('EmployeeTreeTableContext not found');
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});

    const showNeighbourhoodPicker = useFormDialogAction({
        title: `Guess Neighbourhood for ${node.fullName}`,
        description: `Move the employee to the neighbourhood they are most likely to fit in`,
        submitButtonText: 'Move',
        fullWidth: true,
        renderForm: ({form, setForm}: { form: GuessNeighbourhoodForm, setForm: SetState<GuessNeighbourhoodForm> }) => (
            <GuessNeighbourhoodPicker
                form={form}
                setForm={setForm}
                employeeNeighbourhoods={RemoteValue.fromValue(model.modifiedEmployeeNeighbourhood)}
            />
        ),
        action: (openDialog) => openDialog({
            initialValue: {employee: node},
            onSubmit: async (form: GuessNeighbourhoodForm) => {
                const neighbourhood = form.neighbourhood;
                if (!neighbourhood) throw new Error('Please select a neighbourhood');
                await model.moveEmployee(form.employee, [neighbourhood]);
            },
            successMessage: form => `Moved ${form.employee.fullName} to ${form.neighbourhood?.name}`,
            errorMessage: 'Failed to move employee. Please try again later.',
        })
    }, [model]);

    const isMoved = model.isMoved({employee: node});
    return canAdmin ? (
        <Box className={SHOW_ON_HOVER_CLASS_NAME} sx={{
            opacity: isMoved ? 1 : 0,
            display: 'inline-flex',
            alignItems: 'center',
            transition: 'opacity 0.2s ease-in-out',
        }}>
            <Tooltip
                title={isMoved ? "Neighbourhoods changed for this employee" : 'Guess which neighbourhood an employee may belong to'}
                enterDelay={500}
            >
                <IconButton color="warning" size="small" onClick={showNeighbourhoodPicker}>
                    <AgricultureIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Box>
    ) : null;
}

const columns: TableColumnData<TreeRow>[] = [
    {
        key: 'name',
        header: 'Name',
        width: '28em',
        cell: ({node, depth}) => (
            <StyledTableCell match={node.match}>
                <Stack direction="row">
                    <Box sx={{pl: `${(depth) * 3}ch`}}/>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EmployeeAvatar employee={node} sx={{width: '24px', height: '24px'}}/>
                        <EmployeeLink employee={node}/>
                    </Stack>
                </Stack>
            </StyledTableCell>
        ),
    },
    {
        key: 'residence',
        header: 'Residence',
        width: '12em',
        cell: ({node}) => (
            <StyledTableCell match={node.match}>
                {/* Don't use <SiteChip/> here to reduce color pollution */}
                {node.residence.hibobOffice ? (
                    <>
                        {node.residence.workSite.emoji && `${node.residence.workSite.emoji} `}
                        {badgeOrDefault(node.residence.hibobOffice)}
                    </>
                ) : (
                    badgeOrDefault(node.residence.workSite)
                )}
            </StyledTableCell>
        ),
    },
    {
        key: 'team',
        header: 'Team',
        width: '20em',
        cell: ({node}) => (
            <StyledTableCell match={node.match}>
                {node.teamPath && (<TeamComponent teamPath={node.teamPath}/>)}
            </StyledTableCell>
        ),
    },
    {
        key: 'neighbourhoods',
        header: 'Neighbourhoods',
        width: '24em',
        cell: ({node}) => (
            <StyledTableCell match={node.match}>
                <SeatingPlanNeighbourhoodSelect node={node}/>
            </StyledTableCell>
        ),
    },
    {
        key: 'move',
        header: '',
        width: '64px',
        cell: ({node}) => (
            <StyledTableCell match={node.match} sx={{verticalAlign: 'bottom'}}>
                <MoveButton node={node}/>
            </StyledTableCell>
        ),
    },
];

const rowId = (row: TreeRow) => row.node.id;

export function EmployeeTreeTable({hierarchy, model}: {
    hierarchy: HierarchyRoot,
    model: SeatingPlanModel,
}) {
    const rows: TreeRow[] = useMemo(() => flattenTree(hierarchy), [hierarchy]);

    return (
        <EmployeeTreeTableContext value={{model}}>
            <VirtualTable
                columns={columns}
                rows={rows}
                id={rowId}
            />
        </EmployeeTreeTableContext>
    );
}
